import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslate } from 'hooks/useTranslate';
import { styled } from '@mui/system';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import MessageIcon from 'assets/icons/tabler_message.svg?react';
import { submitContactForm } from 'actions/contact';
import { showServiceMessage, addMessage } from 'actions/error';
import { getLanguage } from 'translation';

const TextFieldStyled = styled(TextField)(() => ({
  backgroundColor: '#fff',
  marginBottom: 24,
  '& .MuiInputBase-input': { height: 'unset' },
  // Lift the shrunk label fully above the input border. Without an explicit
  // transform the label sits at the border line and Cyrillic ascenders/the
  // apostrophe in "Ім'я" get visually clipped. Hiding the notch legend
  // keeps the white-background label visually clean over the outline.
  '& .MuiInputLabel-root.MuiInputLabel-shrink': {
    backgroundColor: '#fff',
    padding: '0 6px',
    transform: 'translate(12px, -9px) scale(0.75)',
  },
  '& .MuiOutlinedInput-notchedOutline legend': {
    maxWidth: 0,
  },
}));

const DialogActionsStyled = styled(DialogActions)(() => ({
  padding: 16
}));

const TriggerButton = styled('button', {
  shouldForwardProp: (prop) => prop !== 'transparent'
})(({ fz, theme, transparent }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  background: 'none',
  border: 'none',
  color: transparent ? theme.palette.textPrimary.main : '#fff',
  fontSize: fz || 14,
  fontWeight: 500,
  fontFamily: 'Rubik-Regular',
  cursor: 'pointer',
  padding: 0
}));

const MessageIconStyled = styled(MessageIcon)(() => ({
  width: 22,
  height: 22
}));

const ContactDialog = ({ open, onClose }) => {
  const t = useTranslate('ContactDialog');
  const dispatch = useDispatch();
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [errors, setErrors] = React.useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const next = { name: '', email: '', message: '' };

    const nameTrim = name.trim();
    const emailTrim = email.trim();
    const messageTrim = message.trim();

    if (!nameTrim) next.name = 'required';
    else if (nameTrim.length < 2) next.name = 'too_short';
    else if (nameTrim.length > 100) next.name = 'too_long';

    if (!emailTrim) next.email = 'required';
    else if (!emailRegex.test(emailTrim)) next.email = 'invalid';

    if (!messageTrim) next.message = 'required';
    else if (messageTrim.length < 10) next.message = 'too_short';
    else if (messageTrim.length > 2000) next.message = 'too_long';

    setErrors(next);
    if (next.name || next.email || next.message) return;

    setIsSubmitting(true);

    try {
      await dispatch(
        submitContactForm({
          name: nameTrim,
          email: emailTrim,
          message: messageTrim,
          language: getLanguage()
        })
      );

      setName('');
      setEmail('');
      setMessage('');
      setErrors({ name: '', email: '', message: '' });
      onClose();

      dispatch(
        addMessage({
          message: t('FormSubmittedSuccessfully') || 'Form submitted successfully',
          variant: 'success'
        })
      );
    } catch (error) {
      console.error('Form submission error:', error);

      if (error.status === 422 && error.errors) {
        const apiErrors = {};
        error.errors.forEach((err) => {
          if (err.param === 'name') apiErrors.name = err.msg;
          if (err.param === 'email') apiErrors.email = err.msg;
          if (err.param === 'message') apiErrors.message = err.msg;
        });
        setErrors(apiErrors);
      } else if (error.response && error.response.errors) {
        const apiErrors = {};
        error.response.errors.forEach((err) => {
          if (err.param === 'name') apiErrors.name = err.msg;
          if (err.param === 'email') apiErrors.email = err.msg;
          if (err.param === 'message') apiErrors.message = err.msg;
        });
        setErrors(apiErrors);
      } else if (error.isTemplateError) {
        setName('');
        setEmail('');
        setMessage('');
        setErrors({ name: '', email: '', message: '' });
        onClose();
        dispatch(
          addMessage({
            message:
              t('FormSubmittedSuccessfully') +
              ' ' +
              (t('EmailNotificationFailed') || '(Email notification temporarily unavailable)'),
            variant: 'success'
          })
        );
      } else {
        dispatch(showServiceMessage(error));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} scroll={'body'} fullWidth maxWidth="sm">
      <DialogTitle>{t('ContactUs')}</DialogTitle>
      <form onSubmit={handleSubmit} noValidate>
        <DialogContent>
          <TextFieldStyled
            label={t('Name')}
            value={name}
            onChange={(e) => {
              const v = e.target.value;
              setName(v);
              if (errors.name && v.trim()) setErrors((prev) => ({ ...prev, name: '' }));
            }}
            fullWidth
            error={Boolean(errors.name)}
            helperText={
              errors.name === 'required'
                ? t('RequiredField')
                : errors.name === 'too_short'
                  ? t('NameTooShort') || 'Name must be at least 2 characters'
                  : errors.name === 'too_long'
                    ? t('NameTooLong') || 'Name must be less than 100 characters'
                    : ''
            }
          />
          <TextFieldStyled
            label={t('Email')}
            type="email"
            value={email}
            onChange={(e) => {
              const v = e.target.value;
              setEmail(v);
              if (errors.email) {
                const emailTrim = v.trim();
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (emailTrim && emailRegex.test(emailTrim))
                  setErrors((prev) => ({ ...prev, email: '' }));
              }
            }}
            fullWidth
            error={Boolean(errors.email)}
            helperText={
              errors.email === 'required'
                ? t('RequiredField')
                : errors.email === 'invalid'
                  ? t('InvalidEmail')
                  : ''
            }
          />
          <TextFieldStyled
            label={t('Message')}
            value={message}
            onChange={(e) => {
              const v = e.target.value;
              setMessage(v);
              if (errors.message && v.trim()) setErrors((prev) => ({ ...prev, message: '' }));
            }}
            fullWidth
            multiline
            rows={5}
            error={Boolean(errors.message)}
            helperText={
              errors.message === 'required'
                ? t('RequiredField')
                : errors.message === 'too_short'
                  ? t('MessageTooShort') || 'Message must be at least 10 characters'
                  : errors.message === 'too_long'
                    ? t('MessageTooLong') || 'Message must be less than 2000 characters'
                    : ''
            }
          />
        </DialogContent>
        <DialogActionsStyled>
          <Button variant="outlined" onClick={onClose} disabled={isSubmitting}>
            {t('Cancel')}
          </Button>
          <Button variant="contained" color="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? t('Sending') || 'Sending...' : t('Send')}
          </Button>
        </DialogActionsStyled>
      </form>
    </Dialog>
  );
};

const ContactCTA = ({ transparent, fz }) => {
  const t = useTranslate('ContactDialog');
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <TriggerButton
        transparent={transparent}
        fz={fz}
        aria-label={t('ContactUs')}
        onClick={() => setOpen(true)}>
        <MessageIconStyled />
        {t('ContactUs')}
      </TriggerButton>
      <ContactDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default ContactCTA;
